// What a scheduler said when they booked over a flagged scheduling rule.
//
// One nested extension per overridden rule, so a booking that bypassed three
// rules carries three of them rather than one blob naming three codes. That
// shape is what makes the calibration query trivial: count occurrences of a
// `ruleCode` sub-extension across Appointments and a rule overridden on every
// booking shows up as miscalibrated without parsing anything.

import type { Appointment, Extension } from '@medplum/fhirtypes'
import { APPOINTMENT_SCHEDULING_RULE_OVERRIDE } from '#src/structureDefinitions.ts'

// Sub-extension urls are bare tokens, not absolute urls: FHIR resolves a
// nested extension's `url` against its parent definition, so a fully-qualified
// url here would name a different, non-existent extension.
const RULE_CODE_URL = 'ruleCode'
const CATEGORY_URL = 'category'
const REASON_URL = 'reason'
const NOTES_URL = 'notes'

export interface SchedulingRuleOverride {
  // The rule that was bypassed, matching the engine's rule code vocabulary.
  ruleCode: string
  // The coarse "why" bucket, for grouping across bookings.
  category: string
  // The scheduler's own words. The bucket says which kind of reason; this says
  // the actual one, and is what a calibration reviewer reads.
  reason: string
  // Optional free-text addendum, independent of the required reason above.
  notes?: string
}

export function buildSchedulingRuleOverrideExtension({
  ruleCode,
  category,
  reason,
  notes,
}: SchedulingRuleOverride): Extension {
  return {
    url: APPOINTMENT_SCHEDULING_RULE_OVERRIDE,
    extension: [
      { url: RULE_CODE_URL, valueCode: ruleCode },
      { url: CATEGORY_URL, valueString: category },
      { url: REASON_URL, valueString: reason },
      ...(notes ? [{ url: NOTES_URL, valueString: notes }] : []),
    ],
  }
}

// Every recorded override on an appointment, in the order they were written.
// An entry missing any of the three parts is dropped rather than surfaced
// half-read: a partial override cannot be attributed to a rule or explained,
// so it is not evidence of anything.
export function getSchedulingRuleOverrides(
  appointment: Appointment
): SchedulingRuleOverride[] {
  const overrides: SchedulingRuleOverride[] = []
  for (const extension of appointment.extension ?? []) {
    if (extension.url !== APPOINTMENT_SCHEDULING_RULE_OVERRIDE) {
      continue
    }
    const parts = extension.extension ?? []
    const ruleCode = parts.find((part) => part.url === RULE_CODE_URL)?.valueCode
    const category = parts.find(
      (part) => part.url === CATEGORY_URL
    )?.valueString
    const reason = parts.find((part) => part.url === REASON_URL)?.valueString
    const notes = parts.find((part) => part.url === NOTES_URL)?.valueString
    if (!ruleCode || !category || !reason) {
      continue
    }
    overrides.push({ ruleCode, category, reason, ...(notes && { notes }) })
  }
  return overrides
}
